import { addDays, endOfMonth, min } from "date-fns";
import React from "react";
import { View, ScrollView } from "react-native";
import { BarChart, LineChart } from "react-native-chart-kit";

import i18n from "@/locales/i18n";
import { checkAsset } from "@/utils/transactions/ExchangeCurrencies";
import { formatToFloat } from "@/utils/transactions/FormatTransactionValue";

function formatNumber(value: number): string {
  if (value >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(1).replace(/\.0$/, "")}m`;
  }
  if (value >= 1_000) {
    return `$${(value / 1_000).toFixed(1).replace(/\.0$/, "")}k`;
  }
  return `$${value.toString()}`;
}

function sumByTransactionType(arr: any[], param: string) {
  const fees = [
    "PROCESSING_FEE",
    "ACH_FEE",
    "SWIFT_FEE",
    "WIRE_FEE",
    "CONVERSION_FEE",
  ];

  return arr.reduce((sum, item) => {
    if (param === "INTERNAL_TRANSFER") {
      if (
        item.description === "INTERNAL_TRANSFER" ||
        fees.includes(item.description)
      ) {
        return sum + Math.abs(item.value);
      }
    } else {
      if (
        item.transactionType === param &&
        item.transactionStatus === "POSTED"
      ) {
        return sum + Math.abs(item.value);
      }
    }
    return sum;
  }, 0);
}

function sumByTransferRail(arr: any[], param: string) {
  return arr.reduce((sum, item) => {
    if (
      item.transactionType === "TRANSFER_OUT" &&
      item.rail === param &&
      item.transactionStatus === "POSTED"
    ) {
      return sum + Math.abs(formatToFloat(item.value));
    }
    return sum;
  }, 0);
}

function sumByTransferInRail(arr: any[], param: string) {
  return arr.reduce((sum, item) => {
    if (
      item.transactionType === "FIAT_TRANSFER_IN" &&
      item.rail === param &&
      item.transactionStatus === "POSTED"
    ) {
      return sum + Math.abs(formatToFloat(item.value));
    }
    return sum;
  }, 0);
}

function sumByDateRange(arr: any[], startDate: Date, endDate: Date) {
  return arr.reduce((sum, item) => {
    const transactionDate = new Date(item.createdAt);
    if (
      transactionDate >= startDate &&
      transactionDate <= endDate &&
      item.transactionType === "TRANSFER_OUT" &&
      item.transactionStatus === "POSTED"
    ) {
      return sum + Math.abs(formatToFloat(item.value));
    }
    return sum;
  }, 0);
}

function sumByFeeType(arr: any[], param: string) {
  return arr.reduce((sum, item) => {
    if (item.fee?.type === param && item.fee?.status === "PAID") {
      return sum + formatToFloat(item.fee?.amount);
    }
    return sum;
  }, 0);
}

function sumExchangeSellVolume(arr: any[], asset: string, isAll?: boolean) {
  return arr.reduce((sum, item) => {
    if (isAll) {
      if (item.exchangeType && item.status === "POSTED") {
        return (
          sum +
          Math.abs(formatToFloat(item.sourceDetails?.amountToDebit)) *
            formatToFloat(item.exchangeRate)
        );
      }
    } else {
      if (
        item.exchangeType &&
        item.status === "POSTED" &&
        checkAsset(item.sourceDetails?.assetTypeId) === asset
      ) {
        return (
          sum +
          Math.abs(formatToFloat(item.sourceDetails?.amountToDebit)) *
            formatToFloat(item.exchangeRate)
        );
      }
    }
    return sum;
  }, 0);
}

function sumExchangeBuyVolume(arr: any[], asset: string, isAll?: boolean) {
  return arr.reduce((sum, item) => {
    if (isAll) {
      if (item.exchangeType && item.status === "POSTED") {
        return (
          sum +
          Math.abs(formatToFloat(item.destinationDetails?.amountToCredit)) *
            formatToFloat(item.exchangeRate)
        );
      }
    } else {
      if (
        item.exchangeType &&
        item.status === "POSTED" &&
        checkAsset(item.destinationDetails?.assetTypeId) === asset
      ) {
        return (
          sum +
          Math.abs(formatToFloat(item.destinationDetails?.amountToCredit)) *
            formatToFloat(item.exchangeRate)
        );
      }
    }
    return sum;
  }, 0);
}

export function generateWiresChart(data: any[]) {
  return (
    <View>
      <BarChart
        data={{
          labels: ["Transfers In", "Transfers Out", "Fees"],
          datasets: [
            {
              data: [
                sumByTransactionType(data, "FIAT_TRANSFER_IN"),
                sumByTransactionType(data, "FIAT_TRANSFER_OUT"),
                sumByTransactionType(data, "INTERNAL_TRANSFER"),
              ],
            },
          ],
        }}
        width={560}
        height={340}
        yAxisLabel=""
        yAxisSuffix=""
        yLabelsOffset={-2}
        showBarTops
        showValuesOnTopOfBars
        style={{ borderRadius: 8 }}
        chartConfig={{
          backgroundColor: "#5D0CBA",
          backgroundGradientFrom: "#5D0CBA",
          backgroundGradientTo: "#9261F2",
          barPercentage: 1,
          decimalPlaces: 1,
          color: (opacity = 1) => `rgba(255, 255, 255, ${opacity})`,
          labelColor: (opacity = 1) => `rgba(255, 255, 255, ${opacity})`,
          formatYLabel: (value) => `${formatNumber(parseFloat(value))}`,
          formatTopBarValue: (value) =>
            `${new Intl.NumberFormat(i18n.language, {
              style: "currency",
              currency: "USD",
              maximumFractionDigits: 0,
            }).format(value)}`,
          propsForHorizontalLabels: { fontSize: 10 },
        }}
      />
    </View>
  );
}

export function generateTransfersRailChart(
  data: any[],
  width: any,
  isMobile: boolean
) {
  return (
    <View>
      <BarChart
        data={{
          labels: ["FEDWIRE", "SWIFT"],
          datasets: [
            {
              data: [
                sumByTransferRail(data, "FEDWIRE"),
                sumByTransferRail(data, "SWIFT"),
              ],
            },
          ],
        }}
        width={isMobile ? 600 : width - 100}
        height={400}
        yLabelsOffset={10}
        fromZero
        yAxisLabel=""
        yAxisSuffix=""
        showValuesOnTopOfBars
        chartConfig={{
          backgroundColor: "#5D0CBA",
          backgroundGradientFrom: "#5D0CBA",
          backgroundGradientTo: "#9261F2",
          barPercentage: 1,
          decimalPlaces: 2,
          color: (opacity = 1) => `rgba(255, 255, 255, ${opacity})`,
          labelColor: (opacity = 1) => `rgba(255, 255, 255, ${opacity})`,
          formatYLabel: (value) => `${formatNumber(parseFloat(value))}`,
          formatTopBarValue: (value) =>
            `${new Intl.NumberFormat(i18n.language, {
              style: "currency",
              currency: "USD",
              maximumFractionDigits: 0,
            }).format(value)}`,
          propsForHorizontalLabels: { fontSize: 10 },
        }}
        style={{
          borderRadius: 8,
        }}
      />
    </View>
  );
}

export function generateTransfersInRailChart(
  data: any[],
  width: any,
  isMobile: boolean
) {
  return (
    <View>
      <BarChart
        data={{
          labels: ["FEDWIRE", "SWIFT", "ACH"],
          datasets: [
            {
              data: [
                sumByTransferInRail(data, "FEDWIRE"),
                sumByTransferInRail(data, "SWIFT"),
                sumByTransferInRail(data, "ACH"),
              ],
            },
          ],
        }}
        width={isMobile ? 600 : width - 100}
        height={400}
        yLabelsOffset={10}
        fromZero
        yAxisLabel=""
        yAxisSuffix=""
        showValuesOnTopOfBars
        chartConfig={{
          backgroundColor: "#5D0CBA",
          backgroundGradientFrom: "#5D0CBA",
          backgroundGradientTo: "#9261F2",
          barPercentage: 1,
          decimalPlaces: 2,
          color: (opacity = 1) => `rgba(255, 255, 255, ${opacity})`,
          labelColor: (opacity = 1) => `rgba(255, 255, 255, ${opacity})`,
          formatYLabel: (value) => `${formatNumber(parseFloat(value))}`,
          formatTopBarValue: (value) =>
            `${new Intl.NumberFormat(i18n.language, {
              style: "currency",
              currency: "USD",
              maximumFractionDigits: 0,
            }).format(value)}`,
          propsForHorizontalLabels: { fontSize: 10 },
        }}
        style={{
          borderRadius: 8,
        }}
      />
    </View>
  );
}

export function generateWeeklyVolumeChart(
  data: any[],
  startDate: Date,
  endDate: Date,
  width: any,
  isMobile: boolean
) {
  const weeks = [];
  let currentStartDate = startDate;
  while (currentStartDate < endDate) {
    let currentEndDate = min([addDays(currentStartDate, 6), endDate]);
    if (weeks.length === 3) {
      const lastDayOfMonth = endOfMonth(currentEndDate);
      if (currentEndDate < lastDayOfMonth) {
        currentEndDate = lastDayOfMonth;
      }
    }

    weeks.push({ start: currentStartDate, end: currentEndDate });
    currentStartDate = addDays(currentEndDate, 1);
  }
  const weeklySums = weeks.map((week) => {
    return sumByDateRange(data, week.start, week.end);
  });

  return (
    <ScrollView>
      <LineChart
        data={{
          labels: weeks.map((week, index) => `Week ${index + 1}`),
          datasets: [
            {
              data: weeklySums,
            },
          ],
        }}
        yLabelsOffset={2}
        width={isMobile ? 600 : width - 100}
        height={400}
        formatYLabel={(value) =>
          `${formatNumber(parseFloat(value.toString()))}`
        }
        bezier
        fromZero
        style={{
          borderRadius: 8,
        }}
        chartConfig={{
          backgroundColor: "#5D0CBA",
          backgroundGradientFrom: "#5D0CBA",
          backgroundGradientTo: "#5D0CBA",
          decimalPlaces: 2,
          color: (opacity = 1) => `rgba(255, 255, 255, ${opacity})`,
          labelColor: (opacity = 1) => `rgba(255, 255, 255, ${opacity})`,
          propsForHorizontalLabels: {
            fontSize: 10,
          },
          style: { alignContent: "center", justifyContent: "center" },
          propsForDots: { r: "3" },
        }}
      />
    </ScrollView>
  );
}

export function generateFeesValueChart(
  data: any[],
  width: any,
  isMobile: boolean
) {
  return (
    <View>
      <BarChart
        data={{
          labels: ["FEDWIRE", "ACH", "SWIFT", "EXCHANGE", "PROCESSING"],
          datasets: [
            {
              data: [
                sumByFeeType(data, "WIRE_FEE"),
                sumByFeeType(data, "ACH_FEE"),
                sumByFeeType(data, "SWIFT_FEE"),
                sumByFeeType(data, "CONVERSION_FEE"),
                sumByFeeType(data, "PROCESSING_FEE"),
              ],
            },
          ],
        }}
        width={isMobile ? 600 : width - 100}
        height={400}
        yLabelsOffset={10}
        fromZero
        yAxisLabel=""
        yAxisSuffix=""
        showValuesOnTopOfBars
        chartConfig={{
          backgroundColor: "#5D0CBA",
          backgroundGradientFrom: "#5D0CBA",
          backgroundGradientTo: "#9261F2",
          barPercentage: 1,
          decimalPlaces: 2,
          color: (opacity = 1) => `rgba(255, 255, 255, ${opacity})`,
          labelColor: (opacity = 1) => `rgba(255, 255, 255, ${opacity})`,
          formatYLabel: (value) => `${formatNumber(parseFloat(value))}`,
          formatTopBarValue: (value) =>
            `${new Intl.NumberFormat(i18n.language, {
              style: "currency",
              currency: "USD",
              maximumFractionDigits: 0,
            }).format(value)}`,
          propsForHorizontalLabels: { fontSize: 10 },
          propsForVerticalLabels: { fontSize: 10 },
        }}
        style={{
          borderRadius: 8,
        }}
      />
    </View>
  );
}

export function generateExchangeBuyVolume(
  data: any[],
  width: any,
  isMobile: boolean
) {
  return (
    <View>
      <BarChart
        data={{
          labels: ["All", "USDT", "USDC", "BTC", "ETH"],
          datasets: [
            {
              data: [
                sumExchangeBuyVolume(data, "", true),
                sumExchangeBuyVolume(data, "USDT"),
                sumExchangeBuyVolume(data, "USDC"),
                sumExchangeBuyVolume(data, "BTC"),
                sumExchangeBuyVolume(data, "ETH"),
              ],
            },
          ],
        }}
        width={isMobile ? 600 : width - 100}
        height={400}
        yAxisLabel=""
        yAxisSuffix=""
        yLabelsOffset={10}
        showBarTops
        fromZero
        showValuesOnTopOfBars
        chartConfig={{
          backgroundColor: "#5D0CBA",
          backgroundGradientFrom: "#5D0CBA",
          backgroundGradientTo: "#9261F2",
          barPercentage: 1,
          decimalPlaces: 2,
          color: (opacity = 1) => `rgba(255, 255, 255, ${opacity})`,
          labelColor: (opacity = 1) => `rgba(255, 255, 255, ${opacity})`,
          formatYLabel: (value) => `${formatNumber(parseFloat(value))}`,
          formatTopBarValue: (value) =>
            `${new Intl.NumberFormat(i18n.language, {
              style: "currency",
              currency: "USD",
              maximumFractionDigits: 0,
            }).format(value)}`,
          propsForHorizontalLabels: { fontSize: 10 },
        }}
        style={{
          borderRadius: 8,
        }}
      />
    </View>
  );
}

export function generateExchangeSellVolume(
  data: any[],
  width: any,
  isMobile: boolean
) {
  return (
    <View>
      <BarChart
        data={{
          labels: ["All", "USDT", "USDC", "BTC", "ETH"],
          datasets: [
            {
              data: [
                sumExchangeSellVolume(data, "", true),
                sumExchangeSellVolume(data, "USDT"),
                sumExchangeSellVolume(data, "USDC"),
                sumExchangeSellVolume(data, "BTC"),
                sumExchangeSellVolume(data, "ETH"),
              ],
            },
          ],
        }}
        width={isMobile ? 600 : width - 100}
        height={400}
        yAxisLabel=""
        yAxisSuffix=""
        yLabelsOffset={10}
        showBarTops
        fromZero
        showValuesOnTopOfBars
        chartConfig={{
          backgroundColor: "#5D0CBA",
          backgroundGradientFrom: "#5D0CBA",
          backgroundGradientTo: "#9261F2",
          barPercentage: 1,
          decimalPlaces: 2,
          color: (opacity = 1) => `rgba(255, 255, 255, ${opacity})`,
          labelColor: (opacity = 1) => `rgba(255, 255, 255, ${opacity})`,
          formatYLabel: (value) => `${formatNumber(parseFloat(value))}`,
          formatTopBarValue: (value) =>
            `${new Intl.NumberFormat(i18n.language, {
              style: "currency",
              currency: "USD",
              maximumFractionDigits: 0,
            }).format(value)}`,
          propsForHorizontalLabels: { fontSize: 10 },
        }}
        style={{
          borderRadius: 8,
        }}
      />
    </View>
  );
}
